import React, { useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { Modal } from 'antd'
import {
  title,
  mediumDesktopExactWidth,
  white,
  titleColor,
  accessibilityColors,
} from '@edulastic/colors'

/**
 * Get all focusable elements within a container
 * @param {HTMLElement} container - The container to search within
 * @returns {HTMLElement[]} Array of focusable elements
 */
const getFocusableElements = (container) => {
  if (!container) return []
  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ')
  return Array.from(container.querySelectorAll(focusableSelectors))
}

const CustomModalStyled = ({ children, triggerRef, onCancel, ...props }) => {
  const previousActiveElement = useRef(null)

  // Store the trigger element when modal opens
  useEffect(() => {
    if (props.visible) {
      // Store current active element or use triggerRef as fallback
      previousActiveElement.current =
        triggerRef?.current || document.activeElement
    }
  }, [props.visible, triggerRef])

  // Focus first interactive element when modal opens
  useEffect(() => {
    if (props.visible) {
      setTimeout(() => {
        const modalContent = document.querySelector('.ant-modal-content')
        if (modalContent) {
          // Set tabindex for accessibility
          modalContent.setAttribute('tabindex', '-1')
          modalContent.setAttribute('role', 'dialog')
          modalContent.setAttribute('aria-modal', 'true')

          // Find and focus the first focusable element in the modal body
          const modalBody = modalContent.querySelector('.ant-modal-body')
          const focusableElements = getFocusableElements(modalBody)

          if (focusableElements.length > 0) {
            // Focus the first interactive element
            focusableElements[0].focus()
          } else {
            // Fallback to close button if no focusable elements in body
            const closeButton = modalContent.querySelector('.ant-modal-close')
            if (closeButton) {
              closeButton.focus()
            }
          }
        }
      }, 100) // Increased timeout to ensure modal is fully rendered
    }
  }, [props.visible])

  // Function to return focus to the trigger element
  const returnFocusToTrigger = useCallback(() => {
    if (previousActiveElement.current && previousActiveElement.current.focus) {
      // Use setTimeout to ensure focus is restored after modal animation
      setTimeout(() => {
        previousActiveElement.current.focus()
      }, 0)
    }
  }, [])

  // Handle cancel (ESC key, X button click, overlay click)
  const handleCancel = useCallback(
    (e) => {
      // Return focus to trigger element
      returnFocusToTrigger()
      // Call the original onCancel if provided
      if (onCancel) {
        onCancel(e)
      }
    },
    [onCancel, returnFocusToTrigger]
  )

  // Handle returning focus when modal closes (backup for afterClose)
  const handleAfterClose = useCallback(() => {
    // Return focus to the trigger element (as a fallback)
    returnFocusToTrigger()
    // Call the original afterClose if provided
    if (props.afterClose) {
      props.afterClose()
    }
  }, [props.afterClose, returnFocusToTrigger])

  return (
    <StyledModal
      {...props}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
    >
      {children}
    </StyledModal>
  )
}

export default CustomModalStyled

const StyledModal = styled(Modal)`
  ${({ zIndex }) =>
    zIndex
      ? `.ant-modal-wrap {
          z-index: ${zIndex};
        };`
      : ''}
  min-width: ${(props) => (props.modalWidth ? props.modalWidth : '600px')};
  max-width: ${(props) => (props.modalMaxWidth ? props.modalMaxWidth : 'none')};
  top: ${(props) => (props.top ? props.top : '100px')};
  .ant-modal-content {
    ${(props) =>
      props.modalMinHeight && {
        'min-height': props.modalMinHeight,
      }}
    background: ${(props) => (props.bgColor ? props.bgColor : white)};
    padding: ${(props) => props.padding || '25px 45px'};
    border-radius: ${(props) => props.borderRadius};
    .ant-modal-close {
      color: ${(props) => props.closeIconColor || title};
      top: ${(props) => props.closeTopAlign || '10px'};
      right: ${(props) => props.closeRightAlign || '30px'};
      &:focus {
        outline: 2px solid ${accessibilityColors.theme.blueHover};
      }
      svg {
        width: 24px;
        height: 24px;
      }
    }
    .ant-modal-header {
      padding: ${(props) =>
        props.headerPadding ? props.headerPadding : '0px'};
      background: transparent;
      border: none;
      .ant-modal-title {
        font-size: ${(props) =>
          props.titleFontSize ? props.titleFontSize : '16px'};
        color: ${(props) => (props.titleColor ? props.titleColor : title)};
        font-weight: ${(props) =>
          props.titleFontWeight ? props.titleFontWeight : 700};
        @media (min-width: ${mediumDesktopExactWidth}) {
          font-size: 22px;
        }
      }
    }
    .ant-modal-body {
      display: ${(props) => (props.centerContent ? 'flex' : 'block')};
      align-items: center;
      background: transparent;
      padding: ${(props) =>
        props.bodyPadding ? props.bodyPadding : '25px 0px'};

      & > h4 {
        color: ${title};
        font-size: 14px;
        margin-bottom: 10px;
      }

      p {
        font-size: 14px;
        color: ${titleColor};
        font-weight: 600;
        width: 100%;
        margin-bottom: ${(props) =>
          props.modalBodyPtagBottomMargin
            ? props.modalBodyPtagBottomMargin
            : '10px'};
        &.label {
          font-size: 11px;
          margin-bottom: 5px;
          text-transform: uppercase;
        }
      }
    }
    .ant-modal-footer {
      border: none;
      display: flex;
      justify-content: ${(props) =>
        props.footerJustifyContent ? props.footerJustifyContent : 'center'};
      padding: 0px;
    }
  }
`
export const ModalHeading = styled.h2`
  font-size: ${({ $fontSize }) => $fontSize || '16px'};
  color: ${({ $color }) => $color || title};
  font-weight: ${({ $fontWeight }) => $fontWeight || 700};
  @media (min-width: ${mediumDesktopExactWidth}) {
    font-size: 22px;
  }
  margin: 0px;
`
